/**
 * The daily brief: inbox state, calories, headlines. Pushed by Hermes, never fetched from it.
 *
 * WHAT MAKES THIS PANEL DIFFERENT from every other one on the deck: its data has an age, and the
 * age changes what the number means. "Inbox 0" is worth knowing at 09:00 and worth nothing at
 * 18:00 if nothing has pushed since breakfast. So every slot states when it was last heard from,
 * and a slot that has gone quiet says so instead of presenting a stale figure as current.
 *
 * The three slots render INDEPENDENTLY. Hermes pushes them on different rhythms, so one silent
 * slot must not blank the other two, and a panel that disappeared entirely because the news
 * sweep failed would hide a perfectly good calorie count.
 *
 * Headlines are attacker-influenced text: Hermes summarises mail written by anyone. They are
 * rendered as text through React, never as markup, and their links were scheme-checked on the
 * server (see mo/daily.ts).
 */

import type { JSX } from 'react';
import type { DashboardDaily } from '@rbv/shared';

/** After this, a slot is reported as stale rather than presented as current. */
const STALE_AFTER_MINUTES = 240;

/**
 * How long ago, in words, plus whether that counts as stale.
 *
 * Coarse on purpose: "3h ago" is the useful precision for a brief pushed a few times a day, and
 * "2h 47m ago" invites him to do arithmetic about whether it still counts.
 */
export function freshness(at: string, now = Date.now()): { label: string; stale: boolean } {
  const t = Date.parse(at);
  // An unparseable timestamp is treated as stale rather than hidden. Showing a number with no
  // provenance is the failure this panel is built to avoid.
  if (Number.isNaN(t)) return { label: 'time unknown', stale: true };
  const mins = Math.max(0, Math.round((now - t) / 60_000));
  const label =
    mins < 1 ? 'just now' : mins < 60 ? `${mins}m ago` : `${Math.floor(mins / 60)}h ago`;
  return { label, stale: mins > STALE_AFTER_MINUTES };
}

function Slot({
  label,
  at,
  children,
}: {
  label: string;
  at: string;
  children: React.ReactNode;
}): JSX.Element {
  const f = freshness(at);
  return (
    <div className="daily-slot" data-stale={f.stale}>
      <p className="daily-slot-label">{label}</p>
      <div className="daily-slot-value">{children}</div>
      {/* The age is part of the reading, not a footnote, so it sits with the number. */}
      <p className="daily-slot-age">{f.label}</p>
    </div>
  );
}

export function DailyBrief({ daily }: { daily: DashboardDaily | undefined }): JSX.Element | null {
  // Nothing pushed yet means no panel at all, rather than an empty frame with three dashes. An
  // empty panel for a feature he has not wired up yet reads as broken.
  if (!daily || (daily.inbox === null && daily.calories === null && daily.news === null)) {
    return null;
  }

  const cal = daily.calories;
  // Clamped: over-target is a real and common state, and a bar past 100% would render outside
  // its track.
  const calPct =
    cal && cal.target !== undefined && cal.target > 0
      ? Math.min(100, Math.round((cal.consumed / cal.target) * 100))
      : null;

  return (
    <div className="daily">
      <div className="daily-row">
        {daily.inbox !== null && (
          <Slot label="Inbox" at={daily.inbox.at}>
            <span className="daily-num">{daily.inbox.unread}</span>
            {/*
              "NEW TODAY", not "unread", and the distinction is the honest one.
              
              Inbox Zero sends one webhook per arriving email carrying {from, subject, snippet},
              with no count in it anywhere, and Gmail's own unread figure needs an OAuth grant
              that has not landed. So what the relay can count is ARRIVALS since midnight, which
              is a real number about a real thing. Labelling that "unread" would imply he has not
              read them, which nothing here knows. At zero the count needs words for the same
              reason: a bare 0 beside "Inbox" reads as no data.
            */}
            <span className="daily-unit">
              {daily.inbox.unread === 0
                ? 'nothing new today'
                : daily.inbox.cleared === undefined || daily.inbox.cleared === 0
                  ? 'new today'
                  : `new today, ${daily.inbox.cleared} cleared`}
            </span>
            {daily.inbox.unread === 0 && daily.inbox.cleared !== undefined && daily.inbox.cleared > 0 && (
              <span className="daily-aside">{daily.inbox.cleared} cleared</span>
            )}
          </Slot>
        )}

        {cal !== null && (
          <Slot label="Calories" at={cal.at}>
            <span className="daily-num">{cal.consumed.toLocaleString()}</span>
            {cal.target !== undefined && (
              <span className="daily-unit">of {cal.target.toLocaleString()}</span>
            )}
            {calPct !== null && cal.target !== undefined && (
              <span
                className="daily-bar"
                /* Both roles: the bar is decoration, the text beside it is the fact. Colour and
                   width alone must never be the only carrier of a value. */
                role="img"
                aria-label={`${calPct}% of target`}
              >
                <i style={{ width: `${calPct}%` }} data-over={cal.consumed > cal.target} />
              </span>
            )}
          </Slot>
        )}
      </div>

      {daily.news !== null && daily.news.items.length > 0 && (
        <div className="daily-news">
          <p className="daily-slot-label">
            Key news <span className="daily-slot-age">{freshness(daily.news.at).label}</span>
          </p>
          <ul className="daily-news-list">
            {daily.news.items.map((n, i) => (
              <li key={`${i}-${n.title}`} className="daily-news-item">
                {n.url === undefined ? (
                  <span className="daily-news-title">{n.title}</span>
                ) : (
                  /*
                   * `noopener noreferrer` and a new tab. These URLs came from outside, and MO OS
                   * is an authenticated app: navigating the app's own tab to an attacker-chosen
                   * page, with a live `window.opener`, is the one thing not to do here.
                   */
                  <a
                    className="daily-news-title"
                    href={n.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {n.title}
                  </a>
                )}
                {n.source !== undefined && <span className="daily-news-source">{n.source}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
